/**
 * RICH ROLE TEMPLATES (F4) — catalog of typical church roles with
 * grants READY, and PURE application of template to insertable rows
 * (`app_role` + `permission_grant` con sujeto rol + `account_role` opcional).
 *
 * Template is a STARTING POINT: applying it creates a normal role of
 * church whose grants can be adjusted later in /permissions like any
 * other (F0b engine does not distinguish origin). Grants of scope `group` /
 * `venue` require selecting node on application; if missing, those grants are
 * SKIPPED and reported in `skipped` (UI warns). Full design:
 * docs/ROLE_TEMPLATES.md.
 */

import type { GrantAction, ScopeNodeKind } from "./types";

/** Scopes a template can request (org excluded: superuser only). */
export type TemplateScopeKind = Exclude<ScopeNodeKind, "org">;

export interface RoleTemplateGrant {
  capability: string;
  action: GrantAction;
  scope: TemplateScopeKind;
  /** Inheritance to descendants (default true). */
  inherit?: boolean;
}

export interface RoleTemplate {
  id: string;
  /** Default name of created role (church can rename). */
  name: string;
  description: string;
  grants: readonly RoleTemplateGrant[];
}

/**
 * Catalog of templates. Criteria:
 * - `manage` only where role MODERATES (includes granting that scope+capability).
 * - sensitive data (reflections, private requests) requires `manage` — why
 *   devotional moderator and cell leader have it.
 * - musician and projection director are guiding cases of 🎯 (F0b):
 *   they only read their scope, nothing more.
 */
export const ROLE_TEMPLATES: readonly RoleTemplate[] = [
  {
    id: "pastor",
    name: "Pastor",
    description: "Pastorea la iglesia: personas, grupos, reuniones y oración a su cargo.",
    grants: [
      { capability: "church", action: "manage", scope: "church" },
      { capability: "people", action: "manage", scope: "church" },
      { capability: "groups", action: "manage", scope: "church" },
      { capability: "meetings", action: "manage", scope: "church" },
      { capability: "attendance", action: "manage", scope: "church" },
      { capability: "prayer", action: "manage", scope: "church" },
      { capability: "devotional-tracking", action: "manage", scope: "church" },
      { capability: "songs", action: "read", scope: "church" },
      { capability: "projection", action: "read", scope: "church" },
      { capability: "finances", action: "read", scope: "church" },
      { capability: "permissions", action: "manage", scope: "church" },
    ],
  },
  {
    id: "cell-leader",
    name: "Líder de célula",
    description:
      "Dirige SU grupo: reuniones, asistencia, devocionales (modera reflexiones) y oración.",
    grants: [
      { capability: "groups", action: "write", scope: "group" },
      { capability: "meetings", action: "manage", scope: "group" },
      { capability: "attendance", action: "manage", scope: "group" },
      { capability: "devotional-tracking", action: "manage", scope: "group" },
      { capability: "prayer", action: "manage", scope: "group" },
    ],
  },
  {
    id: "worship-director",
    name: "Director de alabanza",
    description: "Dirige el ministerio de música: biblioteca, ensayos, setlists y proyección.",
    grants: [
      { capability: "songs", action: "manage", scope: "group" },
      { capability: "rehearsals", action: "manage", scope: "group" },
      { capability: "groups", action: "write", scope: "group" },
      { capability: "projection", action: "write", scope: "church" },
    ],
  },
  {
    id: "musician",
    name: "Músico",
    description: "Ve los acordes/notas y ensayos de SU grupo de alabanza — nada más.",
    grants: [
      { capability: "songs", action: "read", scope: "group" },
      { capability: "rehearsals", action: "read", scope: "group" },
    ],
  },
  {
    id: "projection-director",
    name: "Director de proyección",
    description:
      "Proyecta en SU local: solo sincroniza los ítems de proyección de ese local (caso guía del motor).",
    grants: [{ capability: "projection", action: "read", scope: "venue" }],
  },
  {
    id: "treasurer",
    name: "Tesorero",
    description: "Administra las finanzas de la iglesia: fondos, partidas, reportes y export.",
    grants: [{ capability: "finances", action: "manage", scope: "church" }],
  },
  {
    id: "usher",
    name: "Ujier",
    description: "Recibe y marca asistencia en las reuniones de la iglesia.",
    grants: [
      { capability: "attendance", action: "write", scope: "church" },
      { capability: "meetings", action: "read", scope: "church" },
    ],
  },
  {
    id: "devotional-moderator",
    name: "Moderador de devocional",
    description:
      "Modera los devocionales de SU grupo: ve las reflexiones de los miembros y da seguimiento.",
    grants: [{ capability: "devotional-tracking", action: "manage", scope: "group" }],
  },
  {
    id: "public-readonly",
    name: "Solo-lectura pública",
    description: "Ve la información básica y las reuniones de la iglesia, sin editar nada.",
    grants: [
      { capability: "church", action: "read", scope: "church" },
      { capability: "meetings", action: "read", scope: "church" },
    ],
  },
  {
    id: "inventory-keeper",
    name: "Encargado de inventario",
    description: "Administra el inventario: ítems, ubicaciones, bajo-stock y movimientos.",
    grants: [{ capability: "inventory", action: "manage", scope: "church" }],
  },
  {
    id: "cleaning-keeper",
    name: "Encargado de limpieza",
    description: "Gestiona los turnos de limpieza y los consumibles que usa su equipo.",
    grants: [
      { capability: "cleaning-roster", action: "manage", scope: "church" },
      { capability: "inventory", action: "write", scope: "church" },
    ],
  },
];

export function roleTemplateById(id: string): RoleTemplate | undefined {
  return ROLE_TEMPLATES.find((tpl) => tpl.id === id);
}

/** Which extra nodes template requests when applied? */
export function templateNeedsScope(template: RoleTemplate): { group: boolean; venue: boolean } {
  return {
    group: template.grants.some((g) => g.scope === "group"),
    venue: template.grants.some((g) => g.scope === "venue"),
  };
}

export interface ApplyTemplateInput {
  template: RoleTemplate;
  /** Church where role lives (anchor for all `church` grants). */
  churchId: string;
  /** Group node for `group` scope grants (if template has them). */
  groupId?: string | null;
  /** Venue node for `venue` scope grants. */
  venueId?: string | null;
  /** Account to assign role to (optional: template can remain unassigned). */
  userId?: string | null;
  /** ID generator (injected so function remains pure and testable). */
  newId: () => string;
  /** Timestamp ISO (inyectado). */
  now: string;
}

export interface AppliedTemplate {
  role: {
    id: string;
    church_id: string;
    name: string;
    description: string;
    created_at: string;
    updated_at: string;
  };
  grants: {
    id: string;
    subject_kind: "role";
    subject_id: string;
    scope_kind: TemplateScopeKind;
    scope_id: string;
    capability_id: string;
    action: GrantAction;
    effect: "allow";
    inherit: number;
    created_at: string;
    updated_at: string;
  }[];
  assignment: {
    id: string;
    role_id: string;
    user_id: string;
    created_at: string;
    updated_at: string;
  } | null;
  /** Grants omitted due to missing node (UI warns and added manually). */
  skipped: RoleTemplateGrant[];
}

/**
 * Applies template -> insertable rows. PURE: does not touch database; caller
 * inserts via normal write-path (requiring `permissions:manage` for role
 * and `manage` of each scope+capability for grants — "manage includes
 * otorgar", F0b).
 */
export function applyRoleTemplate(input: ApplyTemplateInput): AppliedTemplate {
  const { template, churchId, groupId, venueId, userId, newId, now } = input;
  const roleId = newId();
  const grants: AppliedTemplate["grants"] = [];
  const skipped: RoleTemplateGrant[] = [];
  for (const g of template.grants) {
    const scopeId =
      g.scope === "church" ? churchId : g.scope === "group" ? (groupId ?? "") : (venueId ?? "");
    if (!scopeId) {
      skipped.push(g);
      continue;
    }
    grants.push({
      id: newId(),
      subject_kind: "role",
      subject_id: roleId,
      scope_kind: g.scope,
      scope_id: scopeId,
      capability_id: g.capability,
      action: g.action,
      effect: "allow",
      inherit: (g.inherit ?? true) ? 1 : 0,
      created_at: now,
      updated_at: now,
    });
  }
  return {
    role: {
      id: roleId,
      church_id: churchId,
      name: template.name,
      description: template.description,
      created_at: now,
      updated_at: now,
    },
    grants,
    assignment: userId
      ? { id: newId(), role_id: roleId, user_id: userId, created_at: now, updated_at: now }
      : null,
    skipped,
  };
}
